// @app/web-player/live-votes/live-vote-session-orchestrator.tsx
import React, {
  Fragment,
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {Socket} from 'socket.io-client';
import {
  WebSocketConnector,
  WebSocketHandle,
  EventHandlers,
} from '@app/admin/live-votes/tools/websocket-connector';
import {LiveVote, LiveVoteArtist} from '@app/admin/live-votes/interfaces/live-vote';
import {Trans} from '@ui/i18n/trans';
import clsx from 'clsx';
import {apiClient} from '@common/http/query-client';
import {useQueryClient} from '@tanstack/react-query';
import {LiveVoteProps} from './live-votes-page';
import { FullPageLoader } from '@ui/progress/full-page-loader';

// --- écrans pilotés par l’admin via WS
export type OrchestratorScreen = 'idle' | 'teaser' | 'vote' | 'reactions' | 'ended';

// --- types de config renvoyés par GET live-votes/:id/server/get-config
type PortValue = number | number[];
type Ports = Partial<Record<'admin' | 'displayer' | 'client' | 'vote', PortValue>>;
type ServerState = 'opening' | 'opened' | 'closing' | 'closed' | string;
type ServerConfig = {
  host?: string;                 // ex: "live.kscolta.corsica" ou "192.168.1.69"
  protocol?: 'ws' | 'wss' | string;
  ports?: Ports;                 // ex: { client: [9006], vote: [9007] }
  url?: string;                  // optionnel si URL commune
  urls?: Partial<Record<'client' | 'vote', string>>; // optionnel si URLs dédiées
  state?: ServerState;           // "opened" | "closed" | ...
};
type StartResponsePayload = {
  command?: string | null;
  server?: ServerConfig | null;
};

export interface LiveVoteSessionOrchestratorProps extends LiveVoteProps {
  vote?: LiveVote;
  artist?: Partial<LiveVoteArtist>
  wsHandle?: MutableRefObject<WebSocketHandle | null>
}

// Lazy unique “panel vote” (gère lui-même submit & feedback)
const IdleScreen = React.lazy(() => import('./views/idle-screen'));
const VoteScreen = React.lazy(() => import('./views/vote-screen'));
const TeaserScreen = React.lazy(() => import('./views/teaser-screen'));
const EndedScreen = React.lazy(() => import('./views/ended-screen'));
const ReactionsScreen = React.lazy(() => import('./views/reactions-screen'));

export function LiveVoteSessionOrchestrator({
  vote,
  setFeedback,
}: LiveVoteSessionOrchestratorProps) {
  const queryClient = useQueryClient();

  // --- UI/état
  const [screen, setScreen] = useState<OrchestratorScreen>('idle');
  const [artist, setArtist] = useState<Record<string,any>>({});
  const [info, setInfo] = useState<{message?: string}>({});

  // --- URL WebSocket finale pour l’interface client
  const [clientWsUrl, setClientWsUrl] = useState<string | null>(null);
  const [voteWsUrl, setVoteWsUrl] = useState<string | null>(null);

  // --- connexion socket: prêt quand client connecté
  const [clientConnected, setClientConnected] = useState(false);
  const [voteConnected, setVoteConnected] = useState(false);
  const serverReady = clientConnected && voteConnected;

  // --- refs
  const clientRef = useRef<WebSocketHandle | null>(null);
  const voteRef = useRef<WebSocketHandle | null>(null);
  const destroyedRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);

  // --- polling pour “patienter” tant que le serveur n’est pas lancé
  const pollingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isPollingRef = useRef(false);

  // ---------------- helpers
  const randomPick = (p?: PortValue): number | undefined => {
    if (p == null) return undefined;
    if (Array.isArray(p)) {
      if (!p.length) return undefined;
      const idx = Math.floor(Math.random() * p.length);
      return p[idx];
    }
    return p;
  };

  // ---------------- Handlers iface=client (pilotage d’écran)
  const clientHandlers = useMemo<EventHandlers>(
    () => ({
      'set': (data) => {
        console.log("set", data);

        if(data.screen) setScreen(data.screen);
        if(data.artist) setArtist(data.artist);
        if(data.info) setInfo(data.info);

        if(data.refetch) {
          queryClient.invalidateQueries({queryKey: ['userLiveVotes']});
        }
      },
      'start-session-artist': (artist) => {
        setScreen("reactions");
        setArtist(artist);
      },
      'stop-session-artist': (artist) => {
        setScreen("idle");
        setArtist(artist);
      },
    }),
    [queryClient],
  );
  const voteHandlers = useMemo<EventHandlers>(
    () => ({}),
    [],
  );


  // ---------------- Connexions & erreurs (client)
  const onClientConnect = useCallback((_s: Socket) => {
    setClientConnected(true);
    clientRef.current?.emit('hello');
  }, []);
  const onClientDisconnect = useCallback((_r: any) => setClientConnected(false), []);
  const onClientError = useCallback((err: any) => {
    const m = String(err?.message || '').toLowerCase();
    if (m.includes('refused') || m.includes('transport closed')) {
      setClientConnected(false);
      scheduleNextPoll();
      return;
    }
    console.error('WS client error', err);
    // pas de toast ici (bruit)
  }, []);

  const onVoteConnect = useCallback((_s: Socket) => setVoteConnected(true), []);
  const onVoteDisconnect = useCallback((_r: any) => setVoteConnected(false), []);
  const onVoteError = useCallback((err: any) => {
    const m = String(err?.message || '').toLowerCase();
    if (m.includes('refused') || m.includes('transport closed')) {
      setVoteConnected(false);
      scheduleNextPoll();
      return;
    }
    console.error('WS vote error', err);
  }, [],);

  // ---------------- Construire URL WS (client) depuis la config serveur
  const buildUrlsFromConfig = useCallback(
    (cfg: ServerConfig) => {
      // ⛔ Tant que le serveur n'est pas "opened", on ne construit pas d'URL
      if ((cfg.state as ServerState) !== 'opened') {
        setClientWsUrl(null);
        return;
      }
      
      const base = `${cfg.protocol}://${cfg.host}`;
      const clientPort = randomPick(cfg.ports?.client);
      const votePort = randomPick(cfg.ports?.vote);

      if (!clientWsUrl && base && cfg.ports?.client) setClientWsUrl(`${base}:${clientPort}`);
      if (!voteWsUrl && base && cfg.ports?.vote) setVoteWsUrl(`${base}:${votePort}`);
    },
    [clientWsUrl, voteWsUrl],
  );

  // ---------------- Polling backoff: decorrelated jitter
  const jitter = useCallback(() => {
    const base = 10_000; // 10s
    const cap = 60_000; // 60s
    const prev =
      ((pollingTimerRef.current as any)?._lastDelay as number | undefined) ||
      base;
    const next = Math.min(
      cap,
      Math.floor(base + Math.random() * (prev * 3 - base)),
    );
    if (pollingTimerRef.current)
      (pollingTimerRef.current as any)._lastDelay = next;
    return next;
  }, []);

  const clearPolling = useCallback(() => {
    if (pollingTimerRef.current) {
      clearTimeout(pollingTimerRef.current);
      pollingTimerRef.current = null;
    }
    isPollingRef.current = false;
  }, []);

  const scheduleNextPoll = useCallback(() => {
    if (destroyedRef.current) return;
    if (isPollingRef.current) return; // déjà programmé
    clearPolling();
    isPollingRef.current = true;
    const delay = jitter();
    pollingTimerRef.current = setTimeout(() => {
      isPollingRef.current = false;
      if (pollingTimerRef.current) 
        (pollingTimerRef.current as any)._lastDelay = delay;
      void fetchServerInfo();
    }, delay);
  }, [clearPolling, jitter]);

  // ---------------- Fetch config serveur (avec cancel + retry)
  const fetchServerInfo = useCallback(async () => {
    try {
      abortRef.current?.abort();
      abortRef.current = new AbortController();

      const slugOrId = vote?.id ?? (vote as any)?.slug;
      if (!slugOrId) return;

      const {data} = await apiClient.get(
        `live-votes/${slugOrId}/server/get-config`,
        {signal: abortRef.current.signal as any},
      );
      const payload: StartResponsePayload = (data?.data ?? data) as StartResponsePayload;
      const server = payload?.server;

      // Pas de serveur → repoll
      if (!server) {
        setScreen('idle');
        setClientWsUrl(null);
        scheduleNextPoll();
        return;
      }

      // Serveur connu mais pas encore "opened" → repoll
      if ((server.state as ServerState) !== 'opened') {
        setScreen('idle');
        setClientWsUrl(null);
        scheduleNextPoll();
        return;
      }

      // Serveur ouvert → construire URL
      buildUrlsFromConfig(server);

      // Si pas d'URL exploitable (client), repoll (sécurité)
      const hasClientUrl =
        !!server.urls?.client ||
        !!randomPick(server.ports?.client);
      if (!hasClientUrl) {
        scheduleNextPoll();
      }
    } catch (e: any) {
      if (e?.name === 'CanceledError' || e?.name === 'AbortError') return;
      console.error('fetchServerInfo error', e);
      scheduleNextPoll();
    }
  }, [vote, buildUrlsFromConfig, scheduleNextPoll]);

  // Premier fetch au mount + cleanup
  useEffect(() => {
    destroyedRef.current = false;
    void fetchServerInfo();
    return () => {
      destroyedRef.current = true;
      clearPolling();
      abortRef.current?.abort();
    };
  }, [fetchServerInfo, clearPolling]);

  // Refetch immédiat quand l’onglet redevient visible
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible') {
        clearPolling();
        void fetchServerInfo();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [fetchServerInfo, clearPolling]);

  // Reset du backoff quand socket client connecté
  useEffect(() => {
    if (serverReady) {
      if (pollingTimerRef.current) (pollingTimerRef.current as any)._lastDelay = 10_000;
      clearPolling();
    }
  }, [serverReady, clearPolling]);

  // Tant qu’on n’a pas d’URL client, on n’instancie pas la socket
  const showWaiting = !clientWsUrl || !voteWsUrl;

  return (
    <Fragment>
      {!showWaiting && (
        <Fragment>
          <WebSocketConnector
            ref={ref => (clientRef.current = ref)}
            slugOrId={vote?.id!}
            iface="client"
            space="public"
            serverUrl={clientWsUrl!}
            eventHandlers={clientHandlers}
            onConnect={onClientConnect}
            onDisconnect={onClientDisconnect}
            onError={onClientError}
          />
          <WebSocketConnector
            ref={ref => (voteRef.current = ref)}
            slugOrId={vote?.id!}
            iface="vote"
            space="public"
            serverUrl={voteWsUrl!}
            eventHandlers={voteHandlers}
            onConnect={onVoteConnect}
            onDisconnect={onVoteDisconnect}
            onError={onVoteError}
          />
        </Fragment>
      )}

      {/* Zone d’affichage */}
      <div className="h-full flex items-center justify-center">
        {(showWaiting || !serverReady) && (
          <div className="p-20 text-center opacity-70">
            <Trans message="Patientez, la session va démarrer…" />
          </div>
        )}

        {!showWaiting && serverReady && (
          <div className={clsx('w-full h-full')}>
            <React.Suspense fallback={<FullPageLoader />}>
              {screen === 'idle' && <IdleScreen vote={vote} />}
              {screen === 'teaser' && <TeaserScreen vote={vote} setFeedback={setFeedback} />}
              {screen === 'reactions' && <ReactionsScreen vote={vote} artist={artist as Partial<LiveVoteArtist>} wsHandle={voteRef} setFeedback={setFeedback} displayMode='radial' />}
              {screen === 'vote' && ( <VoteScreen vote={vote} setFeedback={setFeedback} setScreen={setScreen} /> )}
              {screen === 'ended' && <EndedScreen vote={vote} setFeedback={setFeedback} />}
            </React.Suspense>

            {info?.message && (
              <div className="p-12 text-center opacity-70">{info.message}</div>
            )}
          </div>
        )}
      </div>
    </Fragment>
  );
}

export default LiveVoteSessionOrchestrator;
